import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaSearch, FaCopy, FaCheck, FaCheckCircle, FaTimesCircle, FaArrowRight, FaLink } from 'react-icons/fa';
import { RiRefreshFill } from 'react-icons/ri';
import { debounce } from 'helpers/debounce';
import { formatBookingDate, joinWithArray } from 'helpers/utils';
import { EmployeeAvatar } from 'components/common/select/EmployeeBadge';
import Modal from 'components/common/Modal';
import Pagination from 'components/common/Pagination';
import sanitizedMarkup from 'helpers/sanitizedMarkup';
import DateTimeToText from 'helpers/dateTimetoText';
import { formatAuditValue } from './activityValueFormat';

// ── Opla CRM sync log card ────────────────────────────────────────────────────
const OPLA_ACTIONS = new Set(['sync_opla_crm_success', 'sync_opla_crm_failed', 'sync_sales_process_opla_crm']);

const OplaSyncLogCard = ({ activity }) => {
  const isSuccess = activity.action === 'sync_opla_crm_success';
  const isFailed  = activity.action === 'sync_opla_crm_failed';
  const changes   = activity.audited_changes || {};

  const stageTo      = changes.stage?.to   || changes.stage;
  const stageFrom    = changes.stage?.from;
  const oplaId       = changes.opla_id;
  const rawFields    = Array.isArray(changes.synced_fields) ? changes.synced_fields : [];
  // Support both [{name, value}] (new) and ['name'] (legacy string array)
  const fields       = rawFields.map(f => (typeof f === 'object' && f !== null ? f : { name: f, value: null }));
  const errorMsg     = changes.error;

  return (
    <div className={`tw-rounded tw-border tw-p-3 tw-mt-1 ${
      isSuccess ? 'tw-bg-emerald-50 tw-border-emerald-200' :
      isFailed  ? 'tw-bg-red-50 tw-border-red-200' :
                  'tw-bg-blue-50 tw-border-blue-200'
    }`}>
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
        {isSuccess ? <FaCheckCircle size={13} className="tw-text-emerald-500 tw-shrink-0" /> :
         isFailed  ? <FaTimesCircle size={13} className="tw-text-red-500 tw-shrink-0" /> :
                     <FaLink size={11} className="tw-text-blue-500 tw-shrink-0" />}
        <span className={`tw-text-xs tw-font-bold ${
          isSuccess ? 'tw-text-emerald-700' : isFailed ? 'tw-text-red-700' : 'tw-text-blue-700'
        }`}>
          Opla CRM Sync — {isSuccess ? 'Success' : isFailed ? 'Failed' : 'Synced'}
        </span>
        {oplaId && (
          <span className="tw-ml-auto tw-text-[10px] tw-text-slate-400 tw-font-mono">
            ID: {oplaId}
          </span>
        )}
      </div>

      {/* Stage change */}
      {stageTo && (
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1.5">
          {stageFrom && (
            <>
              <span className="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-rounded-xl">{stageFrom}</span>
              <FaArrowRight size={9} className="tw-text-slate-400 tw-shrink-0" />
            </>
          )}
          <span className={`tw-text-[11px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-xl ${
            isSuccess ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-blue-100 tw-text-blue-700'
          }`}>{stageTo}</span>
        </div>
      )}

      {/* Fields synced — show name + value */}
      {fields.length > 0 && (
        <div className="tw-mt-1.5 tw-rounded-md tw-border tw-border-emerald-100 tw-overflow-hidden">
          <div className="tw-px-2 tw-py-1 tw-bg-emerald-50 tw-border-b tw-border-emerald-100">
            <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-emerald-600">
              Fields synced to Opla
            </span>
          </div>
          <div className="tw-divide-y tw-divide-emerald-50">
            {fields.map((f, i) => (
              <div key={i} className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1 tw-bg-white">
                <span className="tw-text-[11px] tw-font-semibold tw-text-slate-600 tw-w-36 tw-shrink-0">{f.name}</span>
                <span className="tw-text-[11px] tw-text-emerald-700 tw-font-medium tw-truncate">
                  {f.value != null ? String(f.value) : <span className="tw-text-slate-300">—</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error detail */}
      {errorMsg && (
        <p className="tw-text-[11px] tw-text-red-600 tw-bg-red-100 tw-rounded-sm tw-px-2 tw-py-1 tw-mb-0 tw-mt-1">{errorMsg}</p>
      )}
    </div>
  );
};
OplaSyncLogCard.propTypes = { activity: PropTypes.object.isRequired };

// ── JSON helpers ──────────────────────────────────────────────────────────────
const parseIfJson = (value) => {
  if (typeof value !== 'string') return value;
  const t = value.trim();
  if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
    try { return JSON.parse(t); } catch { return value; }
  }
  return value;
};

const PRIM_COLORS = { string: '#c3e88d', number: '#82aaff', boolean: '#ffcb6b', null: '#f07178' };

const PrimitiveValue = ({ value }) => {
  if (value === null) return <span style={{ color: PRIM_COLORS.null }}>null</span>;
  const type    = typeof value;
  const color   = PRIM_COLORS[type] || '#cdd6f4';
  const display = type === 'string' ? `"${value}"` : String(value);
  return <span style={{ color }}>{display}</span>;
};
PrimitiveValue.propTypes = { value: PropTypes.any };

const JsonTreeNode = ({ data, name, depth = 0 }) => {
  const isObject = typeof data === 'object' && data !== null;
  const isArray  = Array.isArray(data);
  const [expanded, setExpanded] = useState(depth < 2);
  const indent = depth * 16;

  if (!isObject) {
    return (
      <div style={{ paddingLeft: indent, lineHeight: '1.7' }}>
        {name !== undefined && <span style={{ color: '#c792ea', fontWeight: 500 }}>"{name}": </span>}
        <PrimitiveValue value={data} />
      </div>
    );
  }

  const keys         = isArray ? [...data.keys()] : Object.keys(data);
  const openBracket  = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';
  const summary      = isArray ? `${keys.length} items` : `${keys.length} keys`;

  return (
    <div style={{ paddingLeft: indent, lineHeight: '1.7' }}>
      <span onClick={() => setExpanded(e => !e)} style={{ cursor: 'pointer' }}>
        <span style={{ cursor: 'pointer', userSelect: 'none', color: '#888', fontSize: '0.7em', marginRight: 4 }}>
          {expanded ? '▼' : '▶'}
        </span>
        {name !== undefined && <span style={{ color: '#c792ea', fontWeight: 500 }}>"{name}": </span>}
        <span style={{ color: '#cdd6f4' }}>{openBracket}</span>
        {!expanded && <span style={{ color: '#888', fontSize: '0.85em' }}> {summary} </span>}
        {!expanded && <span style={{ color: '#cdd6f4' }}>{closeBracket}</span>}
      </span>
      {expanded && (
        <>
          {keys.map(key => (
            <JsonTreeNode key={key} data={isArray ? data[key] : data[key]} name={isArray ? String(key) : key} depth={depth + 1} />
          ))}
          <div><span style={{ color: '#cdd6f4' }}>{closeBracket}</span></div>
        </>
      )}
    </div>
  );
};
JsonTreeNode.propTypes = { data: PropTypes.any, name: PropTypes.string, depth: PropTypes.number };

// ── JSON Viewer Modal ─────────────────────────────────────────────────────────
const JsonViewerModal = ({ value, fieldName, showModal, setShowModal }) => {
  const [copied,   setCopied]   = useState(false);
  const isArray    = Array.isArray(value);
  const itemCount  = isArray ? value.length : Object.keys(value).length;

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(value, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const footer = (
    <div className="tw-flex tw-items-center tw-gap-2">
      <button type="button" onClick={handleCopy}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-cursor-pointer tw-transition-colors">
        {copied ? <><FaCheck size={11} /> Copied!</> : <><FaCopy size={11} /> Copy JSON</>}
      </button>
      <button type="button" onClick={() => setShowModal(false)}
        className="tw-inline-flex tw-items-center tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-cursor-pointer tw-transition-colors">
        Close
      </button>
    </div>
  );

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" title={
      <div className="tw-flex tw-items-center tw-gap-2">
        <span>{fieldName}</span>
        <span className={`tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-text-[10px] tw-font-semibold ${isArray ? 'tw-bg-blue-100 tw-text-blue-700' : 'tw-bg-slate-200 tw-text-slate-600'}`}>
          {isArray ? `Array [${itemCount} items]` : `Object {${itemCount} keys}`}
        </span>
      </div>
    } footer={footer}>
      <div style={{ background: '#1e1e2e', color: '#cdd6f4', padding: 16, borderRadius: 6, fontFamily: "'Monaco','Menlo','Courier New',monospace", fontSize: '0.78em', overflowX: 'auto', maxHeight: '60vh', overflowY: 'auto' }}>
        <JsonTreeNode data={value} depth={0} />
      </div>
    </Modal>
  );
};
JsonViewerModal.propTypes = {
  value:        PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  fieldName:    PropTypes.string.isRequired,
  showModal:    PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
};

// ── JSON Summary Badge ────────────────────────────────────────────────────────
const JsonSummaryBadge = ({ value, onClick }) => {
  const isArray = Array.isArray(value);
  const count   = isArray ? value.length : Object.keys(value).length;
  const label   = isArray ? `[ ${count} items ]` : `{ ${count} keys }`;
  return (
    <button type="button" onClick={onClick} title="Click to view formatted JSON"
      className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0">
      <span className={`tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-text-[10px] tw-font-semibold ${isArray ? 'tw-bg-blue-100 tw-text-blue-700' : 'tw-bg-sky-100 tw-text-sky-700'}`}>
        {isArray ? 'Array' : 'Object'} {label}
      </span>
      <span className="tw-text-[10px] tw-text-primary-500">View →</span>
    </button>
  );
};
JsonSummaryBadge.propTypes = {
  value:   PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  onClick: PropTypes.func.isRequired,
};

// ── JSON Viewer ───────────────────────────────────────────────────────────────
const JsonViewer = ({ value, fieldName, formatField }) => {
  const [showModal, setShowModal] = useState(false);
  const parsed = parseIfJson(value);
  const isViewable = typeof parsed === 'object' && parsed !== null &&
    (Array.isArray(parsed) ? parsed.length > 0 : Object.keys(parsed).length > 0);

  if (!isViewable) {
    if (typeof parsed === 'boolean') return <span>{parsed ? 'true' : 'false'}</span>;
    // Dates read as "20-Aug-2026 14:00" rather than the stored ISO string.
    let text = formatField ? formatField(parsed) : formatAuditValue(parsed);
    if (typeof text === 'object' && text !== null) {
      return (
        <>
          <JsonSummaryBadge value={text} onClick={() => setShowModal(true)} />
          <JsonViewerModal value={text} fieldName={fieldName} showModal={showModal} setShowModal={setShowModal} />
        </>
      );
    }
    text = String(text ?? '');
    if (text.length > 120) return <span title={text}>{text.substring(0, 120)}&hellip;</span>;
    return <span>{text}</span>;
  }

  return (
    <>
      <JsonSummaryBadge value={parsed} onClick={() => setShowModal(true)} />
      <JsonViewerModal value={parsed} fieldName={fieldName} showModal={showModal} setShowModal={setShowModal} />
    </>
  );
};
JsonViewer.propTypes = {
  value:       PropTypes.any,
  fieldName:   PropTypes.string.isRequired,
  formatField: PropTypes.func,
};

// ── ActivityHistory ───────────────────────────────────────────────────────────
const ActivityHistory = ({
  activityLogData,
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  getActivityLog,
  ...dynamicParams
}) => {
  const {
    data          = [],
    totalRows     = 0,
    pageSize      = 20,
    totalPagesCount = 1,
    currentParams,
  } = activityLogData || {};

  const key = 'activityLogData';

  const [filterValue, setFilterValue] = useState('');
  const [openStates,  setOpenStates]  = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const dispatch = useDispatch();

  const fetchActivityLog = () => {
    if (currentParams) {
      dispatch(getActivityLog({ ...dynamicParams, params: { ...currentParams, q: filterValue } }));
    }
  };

  // refetch only when currentParams changes: fetchActivityLog closes over dynamicParams (a
  // fresh rest object every render) and filterValue, which would refetch on every keystroke
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchActivityLog(); }, [currentParams]);

  const handlePageChange = (page, size = pageSize) => {
    setCurrentPage(page);
    dispatch(setNestedPage({ key, page }));
    dispatch(setNestedPageSize({ key, pageSize: size }));
    const newParams = { ...currentParams, page, per_page: size };
    dispatch(setNestedParams({ key, params: newParams }));
    dispatch(getActivityLog({ ...dynamicParams, params: newParams }));
  };

  const handleChangeDebounced = useMemo(
    () => debounce((value) => {
      const newParams = value ? { q: value } : { ...currentParams };
      dispatch(setNestedParams({ key, params: newParams }));
    }, 500),
    // one stable debounce instance (recreating it would leave pending calls firing twice);
    // currentParams is intentionally the value captured when it was created
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  const handleChange = (e) => {
    const { value } = e.target;
    setFilterValue(value);
    handleChangeDebounced(value);
  };

  const toggle = (index) => setOpenStates(prev => ({ ...prev, [index]: !prev[index] }));

  const renderValue = (value, fieldName, suffix, formatField) => {
    if (value === null || value === undefined)
      return <span className="tw-text-slate-400">—</span>;
    return <JsonViewer value={value} fieldName={`${fieldName}${suffix}`} formatField={formatField} />;
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-0">
      {/* Header / filter */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-bg-slate-50 tw-border-b tw-border-slate-200 tw-rounded-t-lg tw-flex-wrap">
        <h6 className="tw-text-sm tw-font-bold tw-text-slate-700 tw-mb-0">Activities history</h6>
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <div className="tw-relative tw-w-56">
            <FaSearch size={11} className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 tw-pointer-events-none" />
            <input
              type="text"
              value={filterValue}
              onChange={handleChange}
              placeholder="Search…"
              className="tw-w-full tw-pl-7 tw-pr-3 tw-py-1.5 tw-text-xs tw-border tw-border-slate-300 tw-rounded tw-bg-white tw-text-slate-700 focus:tw-outline-none focus:tw-border-blue-400 focus:tw-ring-1 focus:tw-ring-blue-200 tw-transition-colors placeholder:tw-text-slate-400"
            />
          </div>
          <button type="button" onClick={fetchActivityLog} title="Refresh"
            className="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-bg-sky-500 tw-text-white hover:tw-bg-sky-600 tw-border-0 tw-cursor-pointer tw-transition-colors">
            <RiRefreshFill size={14} />
          </button>
        </div>
      </div>

      {/* Activity list */}
      <div className="tw-divide-y tw-divide-slate-100">
        {data.length === 0 ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12 tw-text-sm tw-text-slate-400">
            No activity found.
          </div>
        ) : data.map((activity, index) => (
          <div key={index} className="tw-flex tw-gap-3 tw-px-4 tw-py-3 hover:tw-bg-slate-50/60 tw-transition-colors">
            {/* Avatar */}
            {activity.avatar?.name && (
              <div className="tw-flex-shrink-0 tw-mt-0.5">
                <EmployeeAvatar name={activity.avatar.name} size={32} fontSize={11} />
              </div>
            )}

            {/* Content */}
            <div className="tw-flex-1 tw-min-w-0">
              <p className="tw-text-xs tw-font-bold tw-text-blue-600 tw-mb-0.5 tw-capitalize">
                {activity.action?.replace(/_/g, ' ')}
              </p>
              <div className="tw-text-xs tw-text-slate-700 tw-mb-1"
                dangerouslySetInnerHTML={sanitizedMarkup(activity.comment)} />
              <p className="tw-text-[10px] tw-text-slate-400 tw-mb-1">
                🕒 <DateTimeToText datetime={activity.created_at} /> · by <span className="tw-font-medium tw-text-slate-500">{activity.created_by}</span>
              </p>

              {/* Opla CRM sync — specialized card */}
              {OPLA_ACTIONS.has(activity.action) && <OplaSyncLogCard activity={activity} />}

              {/* Audit changes — generic table (skip for opla logs which use OplaSyncLogCard) */}
              {!OPLA_ACTIONS.has(activity.action) && activity.audited_changes && Object.keys(activity.audited_changes).length > 0 && (
                <div className="tw-mt-1.5">
                  <Link
                    to="#"
                    onClick={(e) => { e.preventDefault(); toggle(index); }}
                    className="tw-text-xs tw-text-primary-500 hover:tw-text-primary-700"
                  >
                    {openStates[index] ? 'Hide detail ▲' : 'View detail ▼'}
                  </Link>

                  {openStates[index] && (
                    <div className="tw-mt-2 tw-rounded tw-border tw-border-slate-200 tw-overflow-hidden">
                      <div className="tw-overflow-x-auto" style={{ maxHeight: 400 }}>
                        <table className="tw-w-full tw-text-xs" style={{ minWidth: 500 }}>
                          <colgroup>
                            <col style={{ width: '22%' }} />
                            <col style={{ width: '39%' }} />
                            <col style={{ width: '39%' }} />
                          </colgroup>
                          <thead className="tw-sticky tw-top-0 tw-z-10 tw-bg-primary-50 tw-border-b tw-border-primary-200">
                            <tr>
                              {['Attribute changed', 'From', 'To'].map(h => (
                                <th key={h} className="tw-px-3 tw-py-2 tw-text-left tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-primary-600">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(activity.audited_changes).map(([fieldName, fieldChanges]) => {
                              const isDate    = fieldName === 'Date of travel' || fieldName === 'Date of return';
                              const formatFn  = isDate ? formatBookingDate : joinWithArray;
                              return (
                                <tr key={fieldName} className="tw-border-b tw-border-slate-100 even:tw-bg-slate-50">
                                  <td className="tw-px-3 tw-py-2 tw-font-semibold tw-text-slate-700 tw-break-words">{fieldName}</td>
                                  <td className="tw-px-3 tw-py-2 tw-text-blue-600 tw-break-words tw-align-top">
                                    {renderValue(fieldChanges[0], fieldName, ' (from)', formatFn)}
                                  </td>
                                  <td className="tw-px-3 tw-py-2 tw-text-red-600 tw-break-words tw-align-top">
                                    {renderValue(fieldChanges[1], fieldName, ' (to)', formatFn)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalRows > 0 && (
        <div className="tw-px-4 tw-py-3 tw-border-t tw-border-slate-100">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPagesCount}
            totalResults={totalRows}
            perPage={pageSize}
            onPageChange={(p) => handlePageChange(p)}
            onPageSizeChange={(size) => handlePageChange(1, size)}
            pageSizeOptions={[10, 20, 50]}
          />
        </div>
      )}
    </div>
  );
};

ActivityHistory.propTypes = {
  activityLogData:  PropTypes.object.isRequired,
  setNestedPage:    PropTypes.func.isRequired,
  setNestedPageSize: PropTypes.func.isRequired,
  setNestedParams:  PropTypes.func.isRequired,
  getActivityLog:   PropTypes.func.isRequired,
};

export default ActivityHistory;
